/* =============================================================
   Settings.jsx — Admin portal settings
   -------------------------------------------------------------
   Layout:
     1. University Profile  — full-width, same 7/5 layout as
                              PartnerProfile / StudentUniversity.
                              Edit mode unlocks all fields inline.
                              Saved via direct fetch() PATCH to
                              bypass GoTrueClient lock.
     2. Account Settings   — col-span-8 (left)
     3. Exchange Process   — col-span-8 (left)
     4. Notifications      — col-span-8 (left)
     5. Profile card       — col-span-4 (right)
     6. Sign Out           — col-span-4 (right)
     7. Version            — bottom of page (full width, centred)
   ============================================================= */

import { useState, useEffect } from 'react';
import Layout               from '../components/Layout';
import { useAuth }          from '../context/AuthContext';
import { supabase }         from '../lib/supabase';
import ProcessConfigSection    from '../components/ProcessConfigSection';
import DocumentTypesSection   from '../components/DocumentTypesSection';
import ExchangeTermsSection   from '../components/ExchangeTermsSection';


/* ─── CONSTANTS ─────────────────────────────────────────────── */
const COLOR_CLASSES = {
  red:     { bg: 'bg-red-100 text-red-700',         dot: 'bg-red-400'     },
  amber:   { bg: 'bg-amber-100 text-amber-700',     dot: 'bg-amber-400'   },
  blue:    { bg: 'bg-blue-100 text-blue-700',        dot: 'bg-blue-400'    },
  indigo:  { bg: 'bg-indigo-100 text-indigo-700',   dot: 'bg-indigo-400'  },
  slate:   { bg: 'bg-slate-100 text-slate-600',     dot: 'bg-slate-400'   },
  emerald: { bg: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-400' },
  violet:  { bg: 'bg-violet-100 text-violet-700',   dot: 'bg-violet-400'  },
  rose:    { bg: 'bg-rose-100 text-rose-700',       dot: 'bg-rose-400'    },
};
const COLOR_OPTIONS = ['red','amber','blue','indigo','slate','emerald','violet','rose'];

const COUNTRIES = [
  '🇩🇪 Germany', '🇫🇷 France', '🇪🇸 Spain', '🇮🇹 Italy', '🇳🇱 Netherlands',
  '🇸🇪 Sweden', '🇩🇰 Denmark', '🇵🇱 Poland', '🇵🇹 Portugal', '🇧🇪 Belgium',
  '🇬🇧 UK', '🇺🇸 USA', '🇨🇳 China', '🇯🇵 Japan', '🇰🇷 South Korea',
  '🇮🇳 India', '🇧🇷 Brazil', '🇦🇺 Australia', '🇨🇦 Canada', '🇸🇬 Singapore',
  '🇨🇭 Switzerland', '🇦🇹 Austria', '🇨🇿 Czech Republic', '🇭🇺 Hungary',
  '🇳🇴 Norway', '🇫🇮 Finland', '🇮🇪 Ireland', '🇬🇷 Greece',
];


/* ─── HELPERS ───────────────────────────────────────────────── */
function genInitials(name = '') {
  return name.split(' ').filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase();
}
function autoAbbr(name = '') {
  return name.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 3).toUpperCase() || '?';
}

const NOTIF_KEY      = 'unixplore_settings_notif';
const ACAD_YEAR_KEY  = 'unixplore_settings_acad_year';
const NOTIF_DEFAULTS = { documentUploads: true, deadlineReminders: true, newUniversities: true };
function loadNotif() {
  try { return { ...NOTIF_DEFAULTS, ...JSON.parse(localStorage.getItem(NOTIF_KEY) || '{}') }; }
  catch { return NOTIF_DEFAULTS; }
}
function loadAcadYear() { return localStorage.getItem(ACAD_YEAR_KEY) || '2026 / 2027'; }


/* ─── SUB-COMPONENTS ────────────────────────────────────────── */
function SectionDivider({ icon, label }) {
  return (
    <div className="flex items-center gap-4 mb-5 mt-2">
      <div className="h-px flex-1 bg-slate-100" />
      <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-50 border border-slate-100">
        <span className="material-symbols-outlined text-[14px] text-indigo-500">{icon}</span>
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{label}</span>
      </div>
      <div className="h-px flex-1 bg-slate-100" />
    </div>
  );
}

function Toggle({ on, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className={`relative inline-flex items-center w-11 h-6 rounded-full flex-shrink-0 transition-colors duration-200 focus:outline-none ${on ? 'bg-indigo-600' : 'bg-slate-200'}`}
    >
      <span className={`inline-block w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${on ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}
function FieldError({ msg }) {
  if (!msg) return null;
  return <p className="text-xs text-red-500 mt-2 flex items-center gap-1"><span className="material-symbols-outlined text-[13px]">error</span>{msg}</p>;
}


/* ─── MAIN COMPONENT ────────────────────────────────────────── */
export default function Settings() {
  const { user, logout, refreshUser } = useAuth();

  /* ── University / Exchange profile (unified) ─────────────── */
  const [uniForm, setUniForm] = useState({
    name:                    user?.university ?? '',
    country:                 '',
    erasmusCode:             '',
    academicYear:            loadAcadYear(),
    exchange_period:         '',
    campus:                  '',
    business_school:         '',
    language_of_instruction: '',
    contact_emails:          [],
    useful_links:            [],
    key_dates:               [],
  });
  const [uniLoading,          setUniLoading]          = useState(true);
  const [uniEditing,          setUniEditing]          = useState(false);
  const [uniSaving,           setUniSaving]           = useState(false);
  const [uniSaved,            setUniSaved]            = useState(false);
  const [uniError,            setUniError]            = useState('');
  const [profilePublishedAt,  setProfilePublishedAt]  = useState(null);

  /* ── Account ─────────────────────────────────────────────── */
  const [account, setAccount] = useState({ fullName: user?.name ?? '', title: user?.title ?? '', email: user?.email ?? '' });
  const [newPass,       setNewPass]       = useState('');
  const [confirmPass,   setConfirmPass]   = useState('');
  const [showNewPass,   setShowNewPass]   = useState(false);
  const [showConfPass,  setShowConfPass]  = useState(false);
  const [accountSaving, setAccountSaving] = useState(false);
  const [accountSaved,  setAccountSaved]  = useState(false);
  const [accountError,  setAccountError]  = useState('');

  /* ── Notifications ───────────────────────────────────────── */
  const [notif, setNotif] = useState(() => loadNotif());

  const flash = (setSaved) => { setSaved(true); setTimeout(() => setSaved(false), 2500); };
  const setU  = k => v => setUniForm(f => ({ ...f, [k]: v }));

  /* ── Load university on mount ────────────────────────────── */
  useEffect(() => {
    if (user === null && !window._authReady) return;
    if (!user?.universityId) { setUniLoading(false); return; }
    setUniLoading(true);
    (async () => {
      try {
        const { data, error } = await supabase
          .from('universities')
          .select('name, country, erasmus_code, exchange_period, campus, business_school, language_of_instruction, contact_emails, useful_links, key_dates, profile_published_at')
          .eq('id', user.universityId)
          .maybeSingle();
        if (error) console.error('[Settings] fetch:', error.message);
        if (data) {
          setUniForm(f => ({
            ...f,
            name:                    data.name                    ?? '',
            country:                 data.country                 ?? '',
            erasmusCode:             data.erasmus_code            ?? '',
            exchange_period:         data.exchange_period         ?? '',
            campus:                  data.campus                  ?? '',
            business_school:         data.business_school         ?? '',
            language_of_instruction: data.language_of_instruction ?? '',
            contact_emails:          data.contact_emails          ?? [],
            useful_links:            data.useful_links            ?? [],
            key_dates:               data.key_dates               ?? [],
          }));
          setProfilePublishedAt(data.profile_published_at ?? null);
        }
      } catch (err) { console.error('[Settings] fetch unexpected:', err); }
      finally { setUniLoading(false); }
    })();
  }, [user?.universityId, user?.id]);

  /* ── Sync account fields ─────────────────────────────────── */
  useEffect(() => {
    if (!user) return;
    setAccount(v => ({ ...v, fullName: user.name ?? v.fullName, title: user.title ?? v.title, email: user.email ?? v.email }));
  }, [user?.name, user?.title, user?.email]); // eslint-disable-line

  /* ── Array helpers ───────────────────────────────────────── */
  const addContact    = ()       => setUniForm(f => ({ ...f, contact_emails: [...f.contact_emails, { name:'', email:'', role:'' }] }));
  const removeContact = i        => setUniForm(f => ({ ...f, contact_emails: f.contact_emails.filter((_,j) => j!==i) }));
  const updateContact = (i,k,v)  => setUniForm(f => ({ ...f, contact_emails: f.contact_emails.map((c,j) => j===i?{...c,[k]:v}:c) }));
  const addLink       = ()       => setUniForm(f => ({ ...f, useful_links: [...f.useful_links, { label:'', url:'' }] }));
  const removeLink    = i        => setUniForm(f => ({ ...f, useful_links: f.useful_links.filter((_,j) => j!==i) }));
  const updateLink    = (i,k,v)  => setUniForm(f => ({ ...f, useful_links: f.useful_links.map((l,j) => j===i?{...l,[k]:v}:l) }));
  const addDate       = ()       => setUniForm(f => ({ ...f, key_dates: [...f.key_dates, { date:'', label:'', color:'indigo' }] }));
  const removeDate    = i        => setUniForm(f => ({ ...f, key_dates: f.key_dates.filter((_,j) => j!==i) }));
  const updateDate    = (i,k,v)  => setUniForm(f => ({ ...f, key_dates: f.key_dates.map((d,j) => j===i?{...d,[k]:v}:d) }));

  /* ── Save university profile (all fields, direct fetch) ──── */
  const saveUni = async () => {
    if (!user?.universityId) return;
    setUniSaving(true); setUniError('');
    try {
      localStorage.setItem(ACAD_YEAR_KEY, uniForm.academicYear);
      const now = new Date().toISOString();
      const payload = {
        name:                    uniForm.name.trim(),
        country:                 uniForm.country,
        erasmus_code:            uniForm.erasmusCode.trim() || null,
        exchange_period:         uniForm.exchange_period.trim()         || null,
        campus:                  uniForm.campus.trim()                  || null,
        business_school:         uniForm.business_school.trim()         || null,
        language_of_instruction: uniForm.language_of_instruction.trim() || null,
        contact_emails:          uniForm.contact_emails.filter(c => c.email),
        useful_links:            uniForm.useful_links.filter(l => l.label || l.url),
        key_dates:               uniForm.key_dates.filter(d => d.date || d.label),
        // On the very first save, publish the profile so other universities
        // get a notification that this university has joined Unixplore.
        ...(!profilePublishedAt && { profile_published_at: now }),
      };
      const { data: { session } } = await supabase.auth.getSession();
      const jwt = session?.access_token;
      if (!jwt) throw new Error('Session expired — please refresh the page and log in again.');
      const supabaseUrl  = supabase.supabaseUrl;
      const supabaseAnon = supabase.supabaseKey;
      const res = await fetch(
        `${supabaseUrl}/rest/v1/universities?id=eq.${encodeURIComponent(user.universityId)}`,
        { method:'PATCH', headers:{ 'Content-Type':'application/json', 'Authorization':`Bearer ${jwt}`, 'apikey':supabaseAnon, 'Prefer':'return=minimal' }, body: JSON.stringify(payload) }
      );
      if (!res.ok) {
        const text = await res.text();
        let msg = `HTTP ${res.status}`;
        try { msg = JSON.parse(text)?.message ?? msg; } catch {}
        throw new Error(msg);
      }
      await refreshUser();
      // If this was the first save, mark profile as published locally so
      // subsequent saves don't overwrite profile_published_at.
      if (!profilePublishedAt) setProfilePublishedAt(now);
      setUniEditing(false);
      flash(setUniSaved);
    } catch (err) { setUniError(err?.message ?? 'Failed to save.'); }
    finally { setUniSaving(false); }
  };

  /* ── Save account ────────────────────────────────────────── */
  const saveAccount = async () => {
    if (newPass || confirmPass) {
      if (newPass !== confirmPass) { setAccountError('Passwords do not match.'); return; }
      if (newPass.length < 6)      { setAccountError('Password must be at least 6 characters.'); return; }
    }
    setAccountSaving(true); setAccountError('');
    try {
      const { error: pErr } = await supabase.from('profiles').update({ name: account.fullName, title: account.title || null, initials: genInitials(account.fullName) }).eq('id', user.id);
      if (pErr) throw pErr;
      if (account.email !== user.email) { const { error: eErr } = await supabase.auth.updateUser({ email: account.email }); if (eErr) throw eErr; }
      if (newPass) { const { error: wErr } = await supabase.auth.updateUser({ password: newPass }); if (wErr) throw wErr; setNewPass(''); setConfirmPass(''); }
      await refreshUser();
      flash(setAccountSaved);
    } catch (err) { setAccountError(err?.message ?? 'Failed to save account settings.'); }
    finally { setAccountSaving(false); }
  };

  const toggleNotif = (key) => { const u = { ...notif, [key]: !notif[key] }; setNotif(u); localStorage.setItem(NOTIF_KEY, JSON.stringify(u)); };

  const notifItems = [
    { key:'documentUploads',   label:'Document uploads',     desc:'Notify when a student uploads a document that needs review'    },
    { key:'deadlineReminders', label:'Deadline reminders',   desc:'Notify when a student deadline is within 14 days or past due'  },
    { key:'newUniversities',   label:'New universities',     desc:'Notify when a new university joins Unixplore'                  },
  ];

  const displayName  = account.fullName || user?.name  || '—';
  const displayTitle = account.title    || user?.title  || '—';
  const displayUni   = uniForm.name     || user?.university || '—';
  const displayEmail = account.email    || user?.email  || '—';
  const abbr         = autoAbbr(uniForm.name || user?.university || '');
  const links        = uniForm.useful_links ?? [];
  const dates        = uniForm.key_dates    ?? [];


  return (
    <Layout>
      {/* ── Page header ────────────────────────────────────────── */}
      <div className="mb-8">
        <h1 className="font-display text-3xl font-semibold text-[#0b1c30]">Settings</h1>
        <p className="text-[#464555] mt-1">Manage your account, university profile, and platform preferences.</p>
      </div>

      {/* ── Section label: Account ───────────────────────────────── */}
      <SectionDivider icon="manage_accounts" label="Account" />

      {/* ══════════════════════════════════════════════════════════
          ACCOUNT + PROFILE SIDEBAR — 8/4 grid
          ══════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-12 gap-6 mb-6">
        <div className="col-span-8">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px] text-indigo-600">manage_accounts</span>
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">Account Settings</h2>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-5">
                {[{ label:'Full Name', key:'fullName' }, { label:'Title', key:'title', placeholder:'e.g. IO Manager' }].map(({ label, key, placeholder }) => (
                  <div key={key}>
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">{label}</label>
                    <input type="text" value={account[key]} onChange={e => setAccount(v => ({ ...v, [key]: e.target.value }))} placeholder={placeholder}
                      className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 text-[#0b1c30] transition" />
                  </div>
                ))}
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">Email Address</label>
                  <input type="email" value={account.email} onChange={e => setAccount(v => ({ ...v, email: e.target.value }))}
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 text-[#0b1c30] transition" />
                  {account.email !== user?.email && (
                    <p className="text-[11px] text-amber-600 mt-1 flex items-center gap-1">
                      <span className="material-symbols-outlined text-[13px]">info</span>
                      Supabase will send a confirmation to the new email before changing it.
                    </p>
                  )}
                </div>
              </div>
              <div className="pt-2 border-t border-slate-100">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Change Password</p>
                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs text-slate-400 block mb-1.5">New Password</label>
                    <div className="relative">
                      <input type={showNewPass ? 'text' : 'password'} value={newPass} onChange={e => { setNewPass(e.target.value); setAccountError(''); }} placeholder="••••••••"
                        className="w-full px-3 py-2.5 pr-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30] transition" />
                      <button type="button" onClick={() => setShowNewPass(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                        <span className="material-symbols-outlined text-[18px]">{showNewPass ? 'visibility_off' : 'visibility'}</span>
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1.5">Confirm Password</label>
                    <div className="relative">
                      <input type={showConfPass ? 'text' : 'password'} value={confirmPass} onChange={e => { setConfirmPass(e.target.value); setAccountError(''); }} placeholder="••••••••"
                        className="w-full px-3 py-2.5 pr-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30] transition" />
                      <button type="button" onClick={() => setShowConfPass(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                        <span className="material-symbols-outlined text-[18px]">{showConfPass ? 'visibility_off' : 'visibility'}</span>
                      </button>
                    </div>
                  </div>
                </div>
                <FieldError msg={accountError} />
              </div>
              <div className="flex justify-end">
                <button onClick={saveAccount} disabled={accountSaving}
                  className={`px-5 py-2.5 text-sm font-medium rounded-xl transition-all flex items-center gap-2 disabled:opacity-60 ${accountSaved ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-[#4343d5] text-white hover:bg-indigo-700'}`}>
                  {accountSaving
                    ? <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                    : <span className="material-symbols-outlined text-[16px]">{accountSaved ? 'check_circle' : 'save'}</span>}
                  {accountSaving ? 'Saving…' : accountSaved ? 'Saved!' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-4 space-y-6">
          {/* Live profile card */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">Profile</h2>
            </div>
            <div className="p-6 flex flex-col items-center gap-4">
              <div className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold font-display" style={{ backgroundColor: user?.avatarBg ?? '#4343d5' }}>
                {genInitials(displayName)}
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-[#0b1c30]">{displayName}</p>
                <p className="text-xs text-slate-400 mt-0.5">{displayTitle} · {displayUni}</p>
                <p className="text-xs text-slate-400 mt-0.5">{displayEmail}</p>
              </div>
            </div>
          </div>
          {/* Sign Out */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px] text-slate-400">logout</span>
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">Session</h2>
            </div>
            <div className="p-5">
              <button onClick={logout}
                className="w-full py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-2">
                <span className="material-symbols-outlined text-[16px]">logout</span>Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Section label: University Profile ───────────────────── */}
      <SectionDivider icon="account_balance" label="University Profile" />

      {/* ══════════════════════════════════════════════════════════
          UNIVERSITY PROFILE — full width, PartnerProfile layout
          This is exactly how incoming students see your university.
          ══════════════════════════════════════════════════════════ */}
      <div className="mb-6">

        {/* Edit / Save controls */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs text-slate-400">This is how your university appears to incoming exchange students</p>
          <div className="flex items-center gap-2">
            {uniSaved && (
              <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                <span className="material-symbols-outlined text-[14px]">check_circle</span>Saved!
              </span>
            )}
            {uniError && <span className="text-xs text-red-500">{uniError}</span>}
            {!uniEditing ? (
              <button
                onClick={() => setUniEditing(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <span className="material-symbols-outlined text-[16px]">edit</span>Edit Profile
              </button>
            ) : (
              <>
                <button
                  onClick={() => { setUniEditing(false); setUniError(''); }}
                  className="text-sm text-slate-400 hover:text-slate-600 px-3 py-1.5 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveUni}
                  disabled={uniSaving}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-[#4343d5] text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-60 transition-colors"
                >
                  {uniSaving
                    ? <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                    : <span className="material-symbols-outlined text-[15px]">save</span>}
                  {uniSaving ? 'Saving…' : 'Save'}
                </button>
              </>
            )}
          </div>
        </div>

        {uniLoading ? (
          <div className="grid grid-cols-12 gap-6 animate-pulse">
            <div className="col-span-7 h-96 bg-slate-100 rounded-2xl" />
            <div className="col-span-5 space-y-4">
              <div className="h-44 bg-slate-100 rounded-2xl" />
              <div className="h-44 bg-slate-100 rounded-2xl" />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-6">

            {/* ── LEFT col-span-7 ───────────────────────────────── */}
            <div className="col-span-7">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">

                {/* Blue header */}
                <div className="bg-[#4343d5] px-6 py-8 flex items-center gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold font-display text-xl">{abbr}</span>
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-bold text-white">
                      {uniEditing ? (uniForm.name || '—') : (uniForm.name || user?.university || '—')}
                    </h3>
                    <p className="text-indigo-200 text-sm mt-1 flex items-center gap-1.5">
                      <span className="material-symbols-outlined text-[14px]">location_on</span>
                      {uniEditing ? (uniForm.country || '—') : (uniForm.country || '—')}
                    </p>
                  </div>
                  {uniForm.erasmusCode && (
                    <span className="ml-auto text-xs font-semibold bg-white/20 text-white px-3 py-1.5 rounded-full">
                      {uniForm.erasmusCode}
                    </span>
                  )}
                </div>

                {/* Exchange Details */}
                <div className="p-6">
                  {uniEditing ? (
                    <div className="grid grid-cols-2 gap-5">
                      {[
                        { label:'Exchange Period',           key:'exchange_period',         placeholder:'e.g. Sep 2026 — Feb 2027'        },
                        { label:'Campus',                    key:'campus',                  placeholder:'e.g. Berlin, Germany'            },
                        { label:'Faculty / Business School', key:'business_school',         placeholder:'e.g. ESMT Berlin Business School' },
                        { label:'Language of Instruction',   key:'language_of_instruction', placeholder:'e.g. English'                    },
                      ].map(({ label, key, placeholder }) => (
                        <div key={key}>
                          <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">{label}</label>
                          <input type="text" value={uniForm[key]} onChange={e => setU(key)(e.target.value)} placeholder={placeholder}
                            className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30] transition" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                      {[
                        { label:'Exchange Period',           value: uniForm.exchange_period,         icon:'calendar_month' },
                        { label:'Campus',                    value: uniForm.campus,                  icon:null             },
                        { label:'Faculty / Business School', value: uniForm.business_school,         icon:null             },
                        { label:'Language of Instruction',   value: uniForm.language_of_instruction, icon:null             },
                      ].map(({ label, value, icon }) => (
                        <div key={label}>
                          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">{label}</p>
                          <p className="text-sm font-medium text-[#0b1c30] flex items-center gap-1.5">
                            {icon && <span className="material-symbols-outlined text-[14px] text-slate-400">{icon}</span>}
                            {value || '—'}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Who to Contact */}
                <div className="border-t border-slate-100">
                  <div className="px-6 py-4 flex items-center gap-2">
                    <span className="material-symbols-outlined text-[18px] text-indigo-600">contacts</span>
                    <h3 className="font-display text-base font-semibold text-[#0b1c30]">Who to Contact</h3>
                  </div>
                  <div className="px-6 pb-6">
                    {uniEditing ? (
                      <div className="space-y-3">
                        {uniForm.contact_emails.length > 0 && (
                          <div className="flex gap-2 items-center">
                            <div className="flex-1 grid grid-cols-3 gap-2">
                              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide pl-3">Name</span>
                              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide pl-3">Email</span>
                              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide pl-3">Role</span>
                            </div>
                            <span className="w-9" />
                          </div>
                        )}
                        {uniForm.contact_emails.map((c, i) => (
                          <div key={i} className="flex gap-2 items-start">
                            <div className="flex-1 grid grid-cols-3 gap-2">
                              <input value={c.name}  onChange={e => updateContact(i,'name',e.target.value)}  placeholder="Name"                 className="px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30]" />
                              <input value={c.email} onChange={e => updateContact(i,'email',e.target.value)} placeholder="email@uni.edu" type="email" className="px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30]" />
                              <input value={c.role}  onChange={e => updateContact(i,'role',e.target.value)}  placeholder="Role"                 className="px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30]" />
                            </div>
                            <button onClick={() => removeContact(i)} className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-200 transition-colors flex-shrink-0">
                              <span className="material-symbols-outlined text-[16px]">delete</span>
                            </button>
                          </div>
                        ))}
                        <button onClick={addContact} className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 font-medium mt-1">
                          <span className="material-symbols-outlined text-[16px]">add</span>Add contact
                        </button>
                      </div>
                    ) : uniForm.contact_emails.length === 0 ? (
                      <p className="text-sm text-slate-400">No contacts added yet.</p>
                    ) : (
                      <div className="space-y-4">
                        {uniForm.contact_emails.map((c, i) => {
                          const initials = (c.name ?? '?').split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase();
                          return (
                            <div key={i} className="flex items-start gap-4">
                              <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 text-sm font-semibold flex-shrink-0">{initials}</div>
                              <div>
                                <p className="text-sm font-semibold text-[#0b1c30]">{c.name || 'International Office'}</p>
                                {c.role && <p className="text-xs text-slate-400 mb-1.5">{c.role}</p>}
                                <a href={`mailto:${c.email}`} className="text-sm text-indigo-600 font-medium hover:underline flex items-center gap-1">
                                  <span className="material-symbols-outlined text-[14px]">mail</span>{c.email}
                                </a>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* University Settings (edit mode only) */}
                {uniEditing && (
                  <div className="border-t border-slate-100 px-6 py-5">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">University Settings</p>
                    <div className="grid grid-cols-2 gap-5">
                      <div className="col-span-2">
                        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">University Name</label>
                        <input type="text" value={uniForm.name} onChange={e => setU('name')(e.target.value)} placeholder="e.g. ESMT Berlin"
                          className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30] transition" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">Country</label>
                        <select value={uniForm.country} onChange={e => setU('country')(e.target.value)}
                          className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white text-[#0b1c30] cursor-pointer">
                          <option value="">Select country</option>
                          {uniForm.country && !COUNTRIES.includes(uniForm.country) && <option value={uniForm.country}>{uniForm.country}</option>}
                          {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">Erasmus Code</label>
                        <input type="text" value={uniForm.erasmusCode} onChange={e => setU('erasmusCode')(e.target.value)} placeholder="e.g. D BERLIN05"
                          className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30] transition" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">Academic Year</label>
                        <select value={uniForm.academicYear} onChange={e => setU('academicYear')(e.target.value)}
                          className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white text-[#0b1c30] cursor-pointer">
                          <option>2026 / 2027</option>
                          <option>2025 / 2026</option>
                          <option>2024 / 2025</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ── RIGHT col-span-5: Links + Dates ───────────────── */}
            <div className="col-span-5 flex flex-col gap-6">

              {/* Useful Links */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
                  <span className="material-symbols-outlined text-[18px] text-indigo-600">link</span>
                  <h3 className="font-display text-base font-semibold text-[#0b1c30]">Useful Links</h3>
                </div>
                <div className="p-4">
                  {uniEditing ? (
                    <div className="space-y-2.5">
                      {uniForm.useful_links.length > 0 && (
                        <div className="flex gap-1.5 items-center">
                          <span className="w-24 text-xs font-semibold text-slate-400 uppercase tracking-wide pl-2.5">Label</span>
                          <span className="flex-1 text-xs font-semibold text-slate-400 uppercase tracking-wide pl-2.5">URL</span>
                          <span className="w-8" />
                        </div>
                      )}
                      {uniForm.useful_links.map((link, i) => (
                        <div key={i} className="flex gap-1.5 items-center">
                          <input value={link.label} onChange={e => updateLink(i,'label',e.target.value)} placeholder="e.g. Housing Portal" className="w-24 min-w-0 px-2.5 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30]" />
                          <input value={link.url}   onChange={e => updateLink(i,'url',e.target.value)}   placeholder="https://..."          className="flex-1 min-w-0 px-2.5 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30]" />
                          <button onClick={() => removeLink(i)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-200 transition-colors flex-shrink-0">
                            <span className="material-symbols-outlined text-[16px]">delete</span>
                          </button>
                        </div>
                      ))}
                      <button onClick={addLink} className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 font-medium mt-1">
                        <span className="material-symbols-outlined text-[16px]">add</span>Add link
                      </button>
                    </div>
                  ) : links.length === 0 ? (
                    <p className="text-sm text-slate-400 px-3 py-2">No links added yet.</p>
                  ) : (
                    <div className="space-y-1">
                      {links.map((link, i) => (
                        <a key={i} href={link.url || '#'} target="_blank" rel="noreferrer"
                          className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-slate-50 transition-colors group">
                          <span className="material-symbols-outlined text-[18px] text-slate-400 group-hover:text-indigo-500 transition-colors">link</span>
                          <span className="text-sm text-[#464555] group-hover:text-indigo-600 transition-colors">{link.label || link.url}</span>
                          <span className="material-symbols-outlined text-[14px] text-slate-300 ml-auto group-hover:text-indigo-400 transition-colors">open_in_new</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Key Dates */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
                  <span className="material-symbols-outlined text-[18px] text-indigo-600">event</span>
                  <h3 className="font-display text-base font-semibold text-[#0b1c30]">Key Dates</h3>
                </div>
                <div className="p-4">
                  {uniEditing ? (
                    <div className="space-y-2.5">
                      {uniForm.key_dates.length > 0 && (
                        <div className="flex gap-2 items-center px-0.5">
                          <span className="w-20 text-xs font-semibold text-slate-400 uppercase tracking-wide">Date</span>
                          <span className="flex-1 text-xs font-semibold text-slate-400 uppercase tracking-wide">Label</span>
                          <span className="w-24 text-xs font-semibold text-slate-400 uppercase tracking-wide pl-5">Color</span>
                          <span className="w-9" />
                        </div>
                      )}
                      {uniForm.key_dates.map((d, i) => (
                        <div key={i} className="flex gap-2">
                          <input value={d.date}  onChange={e => updateDate(i,'date',e.target.value)}  placeholder="Jun 15"   className="w-20 px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30]" />
                          <input value={d.label} onChange={e => updateDate(i,'label',e.target.value)} placeholder="e.g. Registration Deadline" className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30]" />
                          <select value={d.color ?? 'indigo'} onChange={e => updateDate(i,'color',e.target.value)}
                            className="w-24 px-2 py-2 text-sm border border-slate-200 rounded-xl bg-white text-[#0b1c30] cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-200">
                            {COLOR_OPTIONS.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
                          </select>
                          <button onClick={() => removeDate(i)} className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-200 transition-colors flex-shrink-0">
                            <span className="material-symbols-outlined text-[16px]">delete</span>
                          </button>
                        </div>
                      ))}
                      <button onClick={addDate} className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 font-medium mt-1">
                        <span className="material-symbols-outlined text-[16px]">add</span>Add date
                      </button>
                    </div>
                  ) : dates.length === 0 ? (
                    <p className="text-sm text-slate-400 px-3 py-2">No key dates added yet.</p>
                  ) : (
                    <div className="space-y-1">
                      {dates.map((d, i) => {
                        const c = COLOR_CLASSES[d.color] ?? COLOR_CLASSES.indigo;
                        return (
                          <div key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors">
                            <div className={`flex-shrink-0 whitespace-nowrap text-xs font-bold px-2 py-1 rounded-lg ${c.bg}`}>{d.date}</div>
                            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.dot}`} />
                            <span className="text-sm text-[#464555]">{d.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        )}
      </div>

      {/* ── Section label: Exchange Process ─────────────────────── */}
      <SectionDivider icon="swap_horiz" label="Exchange Process" />

      {/* ══════════════════════════════════════════════════════════
          LOWER SECTION — Exchange Process + Notifications
          ══════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-12 gap-6">

        {/* Row 1 — Exchange Process (left) + Notifications (right) */}
        <div className="col-span-8">
          <ProcessConfigSection />
        </div>

        {/* Notifications — col-span-4, self-start so it doesn't stretch */}
        <div className="col-span-4 self-start">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-[18px] text-indigo-600">notifications</span>
                <h2 className="font-display text-base font-semibold text-[#0b1c30]">Notifications</h2>
              </div>
              <span className="text-xs text-slate-400">Auto-saved</span>
            </div>
            <div className="px-6 pt-4 pb-1">
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5">
                <span className="material-symbols-outlined text-[15px] text-amber-500 mt-0.5 flex-shrink-0">info</span>
                <p className="text-xs text-amber-700 leading-relaxed">We recommend keeping all notifications active to stay on top of your students' process.</p>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {notifItems.map(item => (
                <div key={item.key} className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-medium text-[#0b1c30]">{item.label}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{item.desc}</p>
                  </div>
                  <Toggle on={notif[item.key]} onToggle={() => toggleNotif(item.key)} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Row 2 — Document Types (left) + Exchange Terms + Term Processes (right) */}
        <div className="col-span-12">
          <SectionDivider icon="folder_managed" label="Documents & Terms" />
        </div>
        <div className="col-span-8">
          <DocumentTypesSection />
        </div>

        <div className="col-span-4 self-start">
          <ExchangeTermsSection />
        </div>

      </div>

      {/* ── Version — bottom of page ────────────────────────────── */}
      <div className="mt-8 pb-2 text-center">
        <p className="text-xs text-slate-300">Unixplore © 2026 · All rights reserved</p>
      </div>

    </Layout>
  );
}
